<template>
  <div class="ag">
    <!-- HERO -->
    <header class="ag-hero">
      <div class="ag-hero__inner">
        <div class="ag-hero__main">
          <div class="ag-eyebrow"><i class="bi bi-calendar3"></i> Informes · Por mes</div>
          <div class="d-flex align-items-center gap-2 flex-wrap mt-2">
            <div class="ag-nav">
              <button type="button" :disabled="!mesPrevDisponible" aria-label="Mes anterior" @click="moverMes(-1)">
                <i class="bi bi-chevron-left"></i>
              </button>
              <select v-model="mesSel" aria-label="Elegir mes">
                <option v-for="m in mesesDesc" :key="m.mes" :value="m.mes">{{ nombreMes(m.mes) }}</option>
              </select>
              <button type="button" :disabled="!mesSigDisponible" aria-label="Mes siguiente" @click="moverMes(1)">
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
            <span v-if="esMesEnCurso" class="ag-chip-delta"><i class="bi bi-broadcast"></i> En curso</span>
          </div>
          <div class="ag-hero__cifra">{{ euros(totalMes) }}</div>
          <p class="ag-hero__frase" v-html="fraseHero"></p>
          <div class="d-flex flex-wrap gap-2">
            <span v-if="deltaMesAnterior != null" class="ag-chip-delta" :class="deltaMesAnterior > 0 ? 'sube' : 'baja'">
              <i class="bi" :class="deltaMesAnterior > 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'"></i>
              {{ textoVariacion(deltaMesAnterior) }} vs. {{ nombreMes(mesAnterior(mesSel), { conAno: false }) }}{{ esMesEnCurso ? ' (mismos días)' : '' }}
            </span>
            <span v-if="deltaAnoPasado != null" class="ag-chip-delta" :class="deltaAnoPasado > 0 ? 'sube' : 'baja'">
              <i class="bi bi-arrow-repeat"></i>
              {{ textoVariacion(deltaAnoPasado) }} vs. {{ nombreMes(mesAnterior(mesSel, 12)) }}
            </span>
          </div>
        </div>
        <div class="ag-hero__side">
          <div class="ag-hero__stats">
            <div class="ag-stat">
              <div class="ag-stat__label">Gastos</div>
              <div class="ag-stat__valor">{{ gastosMes.length }}</div>
              <div class="ag-stat__sub">ticket medio {{ euros(gastosMes.length ? totalMes / gastosMes.length : 0, { redondo: true }) }}</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Al día</div>
              <div class="ag-stat__valor">{{ euros(mediaDiariaMes) }}</div>
              <div class="ag-stat__sub">{{ diasTranscurridos }} días</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Día más caro</div>
              <div class="ag-stat__valor">{{ diaMasCaro ? euros(diaMasCaro.total, { redondo: true }) : '—' }}</div>
              <div class="ag-stat__sub">{{ diaMasCaro ? fechaCorta(diaMasCaro.dia) : '' }}</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Top categoría</div>
              <div class="ag-stat__valor">{{ rankingMes[0] ? euros(rankingMes[0].total, { redondo: true }) : '—' }}</div>
              <div class="ag-stat__sub">{{ rankingMes[0]?.categoria || '' }}</div>
            </div>
            <template v-if="hasIngresos">
              <div class="ag-stat">
                <div class="ag-stat__label">Ingresos</div>
                <div class="ag-stat__valor">{{ euros(ingresosMes, { redondo: true }) }}</div>
                <div class="ag-stat__sub">del mes</div>
              </div>
              <div class="ag-stat">
                <div class="ag-stat__label">Balance</div>
                <div class="ag-stat__valor" :style="{ color: ingresosMes - totalMes >= 0 ? '#c8f5d9' : '#ffd3cf' }">
                  {{ euros(ingresosMes - totalMes, { redondo: true }) }}
                </div>
                <div class="ag-stat__sub">{{ ingresosMes ? `ahorro ${(((ingresosMes - totalMes) / ingresosMes) * 100).toFixed(0)}%` : 'sin ingresos' }}</div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </header>

    <div class="ag-body">
      <div v-if="!mesesDesc.length" class="ag-card ag-vacio">Todavía no hay gastos registrados.</div>
      <template v-else>
        <!-- INSIGHTS -->
        <div v-if="insights.length" class="ag-insights">
          <div v-for="(ins, i) in insights" :key="i" class="ag-insight">
            <div class="ag-insight__icono"><i class="bi" :class="ins.icono"></i></div>
            <div class="ag-insight__texto" v-html="ins.html"></div>
          </div>
        </div>

        <div class="ag-grid">
          <!-- PULSO MENSUAL -->
          <section class="ag-card ag-span-8">
            <div class="ag-card__head">
              <div>
                <h2 class="ag-card__titulo">Pulso mensual</h2>
                <div class="ag-leyenda mt-1">
                  <span><i style="background:#1f6b45"></i>Gastos</span>
                  <template v-if="hayIngresos">
                    <span><i style="background:#2a78d6"></i>Ingresos</span>
                  </template>
                  <span style="color:#eb6834"><i class="punteada" style="background:#eb6834"></i>Media {{ euros(mediaVentana, { redondo: true }) }}</span>
                </div>
              </div>
              <div class="ag-seg" role="group" aria-label="Ventana">
                <button v-for="v in ventanas" :key="v.n" type="button" :class="{ activo: ventana === v.n }" @click="ventana = v.n">{{ v.label }}</button>
              </div>
            </div>
            <div class="ag-chart">
              <Bar :data="pulsoData" :options="pulsoOptions" />
            </div>
            <div class="ag-card__sub mt-2"><i class="bi bi-hand-index me-1"></i>Toca una barra para ver ese mes</div>
          </section>

          <!-- CALENDARIO -->
          <section class="ag-card ag-span-4">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo ag-cap">{{ nombreMes(mesSel, { conAno: false }) }} día a día</h2>
              <span class="ag-card__sub">{{ diasConGasto }} días con gasto</span>
            </div>
            <AgCalendarioMes :mes="mesSel" :totales-dia="totalesDiaMes" :seleccionado="diaSel" @seleccionar="diaSel = $event" />
          </section>
        </div>

        <div class="ag-grid">
          <!-- CARRERA DEL MES -->
          <section class="ag-card ag-span-7">
            <div class="ag-card__head">
              <div>
                <h2 class="ag-card__titulo">La carrera del mes</h2>
                <div class="ag-card__sub">Gasto acumulado día a día</div>
              </div>
              <div class="ag-leyenda">
                <span><i style="background:#1f6b45;height:3px"></i>{{ nombreMes(mesSel, { conAno: false }) }}</span>
                <span><i style="background:#2a78d6"></i>{{ nombreMes(mesAnterior(mesSel), { conAno: false }) }}</span>
                <span style="color:#9aa39c"><i class="punteada" style="background:#9aa39c"></i>Media 6 meses</span>
              </div>
            </div>
            <div class="ag-chart">
              <Line :data="carreraData" :options="carreraOptions" />
            </div>
          </section>

          <!-- CATEGORÍAS DEL MES -->
          <section class="ag-card ag-span-5">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">En qué se fue</h2>
              <span class="ag-card__sub">vs. mes anterior</span>
            </div>
            <div v-if="!rankingMes.length" class="ag-vacio">Sin gastos este mes</div>
            <template v-else>
              <div class="ag-tira">
                <span
                  v-for="c in rankingMes"
                  :key="c.categoria"
                  :style="{ flexGrow: c.total, background: colorCat(c.categoria), opacity: catSel && catSel !== c.categoria ? 0.3 : 1 }"
                  :title="`${c.categoria}: ${euros(c.total)}`"
                  @click="toggleCat(c.categoria)"
                ></span>
              </div>
              <ul class="ag-rank mt-2">
                <li
                  v-for="c in rankingMes.slice(0, 7)"
                  :key="c.categoria"
                  class="ag-rank__item"
                  :class="{ activo: catSel === c.categoria, atenuado: catSel && catSel !== c.categoria }"
                  role="button"
                  tabindex="0"
                  @click="toggleCat(c.categoria)"
                  @keydown.enter="toggleCat(c.categoria)"
                >
                  <span class="ag-dot" :style="{ background: colorCat(c.categoria) }"></span>
                  <span class="ag-rank__nombre">{{ c.categoria }}</span>
                  <span class="ag-rank__valor">{{ euros(c.total) }}</span>
                  <span class="ag-rank__barra"><span :style="{ width: `${(c.total / rankingMes[0].total) * 100}%`, background: colorCat(c.categoria) }"></span></span>
                  <span class="ag-rank__meta">
                    <span>{{ c.pct.toFixed(1) }}%</span>
                    <span class="ag-delta" :class="claseDelta(deltaCatMes(c))">
                      {{ catMesAnterior[c.categoria] ? textoVariacion(deltaCatMes(c)) : 'nueva' }}
                    </span>
                  </span>
                </li>
              </ul>
            </template>
          </section>
        </div>

        <div class="ag-grid">
          <!-- PODIO -->
          <section class="ag-card ag-span-5">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">Podio del mes</h2>
              <span class="ag-card__sub">Los gastos más grandes</span>
            </div>
            <div v-if="!podio.length" class="ag-vacio">Sin gastos este mes</div>
            <ol v-else class="ag-podio">
              <li v-for="g in podio" :key="g.id">
                <div class="min-w-0">
                  <div class="ag-podio__concepto">{{ g.concepto || 'Sin concepto' }}</div>
                  <div class="ag-podio__meta">
                    <span class="ag-dot" :style="{ background: colorCat(categoriaDe(g)) }"></span>
                    {{ categoriaDe(g) }} · {{ fechaCorta(diaISO(g)) }}
                  </div>
                </div>
                <div class="ag-cifra fs-6">{{ euros(importe(g)) }}</div>
              </li>
            </ol>
          </section>

          <!-- HISTÓRICO DE MESES -->
          <section class="ag-card ag-span-7">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">Todos los meses</h2>
              <span class="ag-card__sub">{{ mesesDesc.length }} meses · media {{ euros(mediaHistorica, { redondo: true }) }}</span>
            </div>
            <div class="ag-meses">
              <button
                v-for="m in mesesDesc.slice(0, limiteMeses)"
                :key="m.mes"
                type="button"
                class="ag-meses__fila"
                :class="{ activo: m.mes === mesSel }"
                @click="mesSel = m.mes"
              >
                <span class="ag-meses__nombre text-capitalize">{{ nombreMes(m.mes, { corto: true }) }}</span>
                <span class="ag-meses__barra">
                  <span :style="{ width: `${(m.total / maxMes) * 100}%` }" :class="{ alto: m.total > mediaHistorica * 1.15 }"></span>
                  <i :style="{ left: `${(mediaHistorica / maxMes) * 100}%` }"></i>
                </span>
                <span class="ag-cifra ag-meses__valor">{{ euros(m.total, { redondo: true }) }}</span>
                <span v-if="hayIngresos" class="ag-meses__balance" :class="(ingresosPorMes[m.mes] || 0) - m.total >= 0 ? 'pos' : 'neg'">
                  {{ euros((ingresosPorMes[m.mes] || 0) - m.total, { redondo: true }) }}
                </span>
              </button>
            </div>
            <div v-if="mesesDesc.length > limiteMeses" class="text-center mt-2">
              <button type="button" class="ag-btn" @click="limiteMeses += 12">Ver más meses</button>
            </div>
            <div v-if="hayIngresos" class="ag-card__sub mt-2">La columna derecha es el balance (ingresos − gastos).</div>
          </section>
        </div>

        <!-- LISTA -->
        <AgListaGastos :gastos="gastosLista" :colores="colores" :titulo="tituloLista">
          <template #filtros>
            <span v-if="diaSel" class="ag-filtro-activo">
              {{ fechaCorta(diaSel) }}
              <button type="button" aria-label="Quitar filtro de día" @click="diaSel = null"><i class="bi bi-x"></i></button>
            </span>
            <span v-if="catSel" class="ag-filtro-activo">
              {{ catSel }}
              <button type="button" aria-label="Quitar filtro de categoría" @click="catSel = null"><i class="bi bi-x"></i></button>
            </span>
          </template>
        </AgListaGastos>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import axios from '../config/axios';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { Bar, Line } from 'vue-chartjs';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  LineController, BarController, Tooltip, Filler, Legend
} from 'chart.js';
import {
  importe, diaISO, categoriaDe, euros, sumar, variacion, textoVariacion, agruparPor, claveMes,
  mapaColoresCategorias, rankingCategorias, mesAnterior, nombreMes, diasDelMes, acumuladoDiarioMes,
  totalesPorDia, aISO, parseISO, diaSemanaLunes, MESES_CORTOS, COLOR_OTRAS
} from '../utils/analisisGastos';
import AgListaGastos from '../components/analisis/AgListaGastos.vue';
import AgCalendarioMes from '../components/analisis/AgCalendarioMes.vue';
import '../assets/analisis-gastos.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, LineController, BarController, Tooltip, Filler, Legend);

const escapar = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default {
  name: 'GastosPorMesView',
  components: { Bar, Line, AgListaGastos, AgCalendarioMes },
  setup() {
    const store = useStore();
    const route = useRoute();
    const hasIngresos = computed(() => store.getters['auth/hasIngresos']);
    const ingresosRaw = ref([]);

    const todos = computed(() => store.getters.getGastos || []);
    const colores = computed(() => mapaColoresCategorias(todos.value));
    const colorCat = (c) => colores.value[c] || COLOR_OTRAS;

    const hoyISO = aISO(new Date());
    const mesHoy = hoyISO.slice(0, 7);

    // Todos los meses desde el primer gasto hasta hoy (con ceros incluidos).
    const porMes = computed(() => agruparPor(todos.value, claveMes));
    const mesesAsc = computed(() => {
      const claves = [...porMes.value.keys()].filter((m) => m <= mesHoy).sort();
      if (!claves.length) return [];
      const out = [];
      for (let m = claves[0]; m <= mesHoy; m = mesAnterior(m, -1)) {
        const x = porMes.value.get(m);
        out.push({ mes: m, total: x?.total || 0, num: x?.num || 0 });
      }
      return out;
    });
    const mesesDesc = computed(() => [...mesesAsc.value].reverse());
    const totalDe = (m) => porMes.value.get(m)?.total || 0;

    const ingresosPorMes = computed(() => {
      const m = {};
      ingresosRaw.value.forEach((i) => { m[i.mes] = i.total; });
      return m;
    });
    const hayIngresos = computed(() => hasIngresos.value && ingresosRaw.value.length > 0);

    // ---------- Selección ----------
    const mesQuery = typeof route.query.mes === 'string' && /^\d{4}-\d{2}$/.test(route.query.mes) ? route.query.mes : null;
    const mesSel = ref(mesQuery && mesQuery <= mesHoy ? mesQuery : mesHoy);
    const diaSel = ref(null);
    const catSel = ref(null);
    watch(mesSel, () => { diaSel.value = null; catSel.value = null; });
    watch(mesesDesc, (lista) => {
      if (lista.length && !lista.some((m) => m.mes === mesSel.value)) mesSel.value = lista[0].mes;
    });

    const idxSel = computed(() => mesesAsc.value.findIndex((m) => m.mes === mesSel.value));
    const mesPrevDisponible = computed(() => idxSel.value > 0);
    const mesSigDisponible = computed(() => idxSel.value >= 0 && idxSel.value < mesesAsc.value.length - 1);
    const moverMes = (d) => {
      const m = mesesAsc.value[idxSel.value + d];
      if (m) mesSel.value = m.mes;
    };
    const toggleCat = (c) => { catSel.value = catSel.value === c ? null : c; };

    const esMesEnCurso = computed(() => mesSel.value === mesHoy);
    const gastosMes = computed(() => todos.value.filter((g) => diaISO(g).startsWith(mesSel.value)));
    const totalMes = computed(() => sumar(gastosMes.value));
    const ingresosMes = computed(() => ingresosPorMes.value[mesSel.value] || 0);
    const diasTranscurridos = computed(() => (esMesEnCurso.value ? parseInt(hoyISO.slice(8, 10), 10) : diasDelMes(mesSel.value)));
    const mediaDiariaMes = computed(() => totalMes.value / Math.max(1, diasTranscurridos.value));
    const totalesDiaMes = computed(() => totalesPorDia(gastosMes.value));
    const diasConGasto = computed(() => Object.values(totalesDiaMes.value).filter((v) => v > 0).length);
    const diaMasCaro = computed(() => {
      let best = null;
      Object.entries(totalesDiaMes.value).forEach(([dia, total]) => { if (!best || total > best.total) best = { dia, total }; });
      return best;
    });
    const rankingMes = computed(() => rankingCategorias(gastosMes.value));
    const catMesAnterior = computed(() => {
      const prev = mesAnterior(mesSel.value);
      const m = {};
      rankingCategorias(todos.value.filter((g) => diaISO(g).startsWith(prev))).forEach((c) => { m[c.categoria] = c.total; });
      return m;
    });
    const deltaCatMes = (c) => variacion(c.total, catMesAnterior.value[c.categoria]);
    const claseDelta = (v) => (v == null ? 'neutro' : v > 0.5 ? 'sube' : v < -0.5 ? 'baja' : 'neutro');

    // Comparativas: si el mes está en curso, se compara con los mismos días del mes anterior.
    const totalHastaDia = (mes, dia) => {
      const acc = acumuladoDiarioMes(todos.value, mes);
      return acc[Math.min(dia, acc.length) - 1] || 0;
    };
    const deltaMesAnterior = computed(() => {
      const prev = mesAnterior(mesSel.value);
      const base = esMesEnCurso.value ? totalHastaDia(prev, diasTranscurridos.value) : totalDe(prev);
      return variacion(totalMes.value, base);
    });
    const deltaAnoPasado = computed(() => {
      const prev = mesAnterior(mesSel.value, 12);
      if (!porMes.value.has(prev)) return null;
      const base = esMesEnCurso.value ? totalHastaDia(prev, diasTranscurridos.value) : totalDe(prev);
      return variacion(totalMes.value, base);
    });

    const ultimos12 = computed(() => {
      const fin = idxSel.value;
      return mesesAsc.value.slice(Math.max(0, fin - 12), fin);
    });
    const media12 = computed(() => (ultimos12.value.length ? ultimos12.value.reduce((s, m) => s + m.total, 0) / ultimos12.value.length : 0));
    const proyeccion = computed(() => mediaDiariaMes.value * diasDelMes(mesSel.value));

    const fraseHero = computed(() => {
      if (!gastosMes.value.length) return 'Aún no hay gastos este mes. ¡Buen comienzo!';
      if (esMesEnCurso.value) {
        const vsMedia = variacion(proyeccion.value, media12.value);
        let txt = `Llevas <strong>${diasTranscurridos.value} días</strong>; a este ritmo cerrarás en <strong>~${euros(proyeccion.value, { redondo: true })}</strong>`;
        if (vsMedia != null) txt += `, ${Math.abs(vsMedia).toFixed(0)}% ${vsMedia > 0 ? 'por encima' : 'por debajo'} de tu media.`;
        return `${txt}`;
      }
      const v = variacion(totalMes.value, media12.value);
      if (v == null) return `Gastaste <strong>${euros(totalMes.value)}</strong> en ${gastosMes.value.length} movimientos.`;
      return `Un mes <strong>${Math.abs(v).toFixed(0)}% ${v > 0 ? 'por encima' : 'por debajo'}</strong> de tu media de los 12 meses previos (${euros(media12.value, { redondo: true })}).`;
    });

    // ---------- Insights ----------
    const insights = computed(() => {
      const out = [];
      if (!gastosMes.value.length) return out;
      // Racha sin gastar
      let racha = 0;
      let mejor = 0;
      for (let d = 1; d <= diasTranscurridos.value; d++) {
        const iso = `${mesSel.value}-${String(d).padStart(2, '0')}`;
        if (!totalesDiaMes.value[iso]) { racha += 1; mejor = Math.max(mejor, racha); } else racha = 0;
      }
      if (mejor >= 2) {
        out.push({ icono: 'bi-emoji-sunglasses', html: `Tu mejor racha: <strong>${mejor} días seguidos</strong> sin gastar nada.` });
      } else {
        out.push({ icono: 'bi-lightning-charge', html: `Has gastado en <strong>${diasConGasto.value} de ${diasTranscurridos.value} días</strong>: casi todos los días cae algo.` });
      }
      // Fin de semana vs laborables
      let finde = 0;
      gastosMes.value.forEach((g) => { if (diaSemanaLunes(diaISO(g)) >= 5) finde += importe(g); });
      if (totalMes.value > 0) {
        const pct = (finde / totalMes.value) * 100;
        out.push({
          icono: 'bi-cup-hot',
          html: `El fin de semana se lleva el <strong>${pct.toFixed(0)}%</strong> del gasto (${euros(finde, { redondo: true })}), ${pct > 28.6 ? 'más' : 'menos'} de lo que le tocaría por días.`
        });
      }
      // Categoría que más cambia
      const prev = catMesAnterior.value;
      const cambios = rankingMes.value.map((c) => ({ categoria: c.categoria, dif: c.total - (prev[c.categoria] || 0) }));
      const sube = cambios.reduce((m, c) => (c.dif > (m?.dif ?? 0) ? c : m), null);
      if (sube && Object.keys(prev).length) {
        out.push({ icono: 'bi-graph-up-arrow', html: `<strong>${escapar(sube.categoria)}</strong> es lo que más crece: <strong>+${euros(sube.dif, { redondo: true })}</strong> respecto a ${nombreMes(mesAnterior(mesSel.value), { conAno: false })}.` });
      }
      // Ranking histórico del mes
      if (!esMesEnCurso.value && mesesAsc.value.length > 3) {
        const orden = [...mesesAsc.value].sort((a, b) => b.total - a.total);
        const pos = orden.findIndex((m) => m.mes === mesSel.value) + 1;
        out.push({
          icono: 'bi-bar-chart-steps',
          html: pos <= orden.length / 2
            ? `Es tu <strong>${pos}º mes más caro</strong> de ${orden.length}.`
            : `Es tu <strong>${orden.length - pos + 1}º mes más barato</strong> de ${orden.length}.`
        });
      } else if (diaMasCaro.value) {
        out.push({ icono: 'bi-fire', html: `El día más intenso fue el <strong>${fechaCorta(diaMasCaro.value.dia)}</strong> con ${euros(diaMasCaro.value.total, { redondo: true })}.` });
      }
      return out.slice(0, 4);
    });

    // ---------- Pulso mensual ----------
    const ventanas = [{ n: 12, label: '12 m' }, { n: 24, label: '24 m' }, { n: 0, label: 'Todo' }];
    const ventana = ref(12);
    const mesesVentana = computed(() => {
      const len = mesesAsc.value.length;
      const n = ventana.value;
      if (!n) return mesesAsc.value;
      // La ventana acaba en el mes elegido, salvo que esté entre los N más recientes.
      const fin = Math.max(idxSel.value, 0);
      const finVista = fin >= len - n ? len : fin + 1;
      return mesesAsc.value.slice(Math.max(0, finVista - n), finVista);
    });
    const mediaVentana = computed(() => {
      const cerrados = mesesVentana.value.filter((m) => m.mes !== mesHoy);
      return cerrados.length ? cerrados.reduce((s, m) => s + m.total, 0) / cerrados.length : 0;
    });

    const etiquetaMes = (m) => `${MESES_CORTOS[parseInt(m.slice(5), 10) - 1]} '${m.slice(2, 4)}`;

    const pulsoData = computed(() => {
      const meses = mesesVentana.value;
      const datasets = [
        {
          label: 'Gastos',
          data: meses.map((m) => m.total),
          backgroundColor: meses.map((m) => (m.mes === mesSel.value ? '#1f6b45' : m.mes === mesHoy ? 'rgba(31,107,69,0.35)' : 'rgba(31,107,69,0.55)')),
          hoverBackgroundColor: '#163528',
          borderRadius: 4,
          borderSkipped: 'bottom',
          maxBarThickness: 34,
          order: 2
        }
      ];
      if (hayIngresos.value) {
        datasets.push({
          label: 'Ingresos',
          data: meses.map((m) => ingresosPorMes.value[m.mes] || 0),
          backgroundColor: meses.map((m) => (m.mes === mesSel.value ? '#2a78d6' : 'rgba(42,120,214,0.45)')),
          borderRadius: 4,
          borderSkipped: 'bottom',
          maxBarThickness: 34,
          order: 2
        });
      }
      datasets.push({
        label: 'Media',
        type: 'line',
        data: meses.map(() => mediaVentana.value),
        borderColor: '#eb6834',
        borderDash: [6, 4],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        order: 1
      });
      return { labels: meses.map((m) => etiquetaMes(m.mes)), datasets };
    });

    const pulsoOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      interaction: { mode: 'index', intersect: false },
      onClick: (_e, els) => {
        if (els.length) mesSel.value = mesesVentana.value[els[0].index].mes;
      },
      onHover: (e, els) => { if (e.native?.target) e.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item) => item.dataset.label !== 'Media',
          callbacks: {
            title: (items) => nombreMes(mesesVentana.value[items[0].dataIndex].mes),
            label: (ctx) => ` ${ctx.dataset.label}: ${euros(ctx.raw)}`,
            afterBody: (items) => {
              const m = mesesVentana.value[items[0].dataIndex];
              const v = variacion(m.total, mediaVentana.value);
              const lineas = [];
              if (v != null) lineas.push(`${textoVariacion(v)} vs. media`);
              if (hayIngresos.value) lineas.push(`Balance: ${euros((ingresosPorMes.value[m.mes] || 0) - m.total)}`);
              return lineas;
            }
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#5c675e', maxRotation: 0, autoSkip: true, autoSkipPadding: 8 } },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(22, 53, 40, 0.06)' },
          ticks: { color: '#5c675e', callback: (v) => euros(v, { redondo: true }) }
        }
      }
    }));

    // ---------- Carrera del mes ----------
    const carreraData = computed(() => {
      const n = diasDelMes(mesSel.value);
      const actual = acumuladoDiarioMes(todos.value, mesSel.value);
      const serieActual = actual.map((v, i) => (esMesEnCurso.value && i + 1 > diasTranscurridos.value ? null : v));
      const prev = acumuladoDiarioMes(todos.value, mesAnterior(mesSel.value));
      const medias = [];
      for (let k = 1; k <= 6; k++) medias.push(acumuladoDiarioMes(todos.value, mesAnterior(mesSel.value, k)));
      const media = Array.from({ length: n }, (_, i) =>
        medias.reduce((s, serie) => s + (serie[Math.min(i, serie.length - 1)] || 0), 0) / medias.length
      );
      return {
        labels: Array.from({ length: n }, (_, i) => i + 1),
        datasets: [
          {
            label: nombreMes(mesSel.value, { conAno: false }),
            data: serieActual,
            borderColor: '#1f6b45',
            backgroundColor: 'rgba(31,107,69,0.12)',
            fill: 'origin',
            borderWidth: 3,
            tension: 0.25,
            pointRadius: 0,
            pointHoverRadius: 5
          },
          {
            label: nombreMes(mesAnterior(mesSel.value), { conAno: false }),
            data: Array.from({ length: n }, (_, i) => prev[Math.min(i, prev.length - 1)]),
            borderColor: '#2a78d6',
            borderWidth: 2,
            tension: 0.25,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false
          },
          {
            label: 'Media 6 meses',
            data: media,
            borderColor: '#9aa39c',
            borderDash: [5, 4],
            borderWidth: 2,
            tension: 0.25,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false
          }
        ]
      };
    });

    const carreraOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 800 },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => `Día ${items[0].label}`,
            label: (ctx) => (ctx.raw == null ? null : ` ${ctx.dataset.label}: ${euros(ctx.raw)}`)
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#5c675e', maxTicksLimit: 8 } },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(22, 53, 40, 0.06)' },
          ticks: { color: '#5c675e', callback: (v) => euros(v, { redondo: true }) }
        }
      }
    };

    // ---------- Podio / histórico ----------
    const podio = computed(() => [...gastosMes.value].sort((a, b) => importe(b) - importe(a)).slice(0, 5));
    const limiteMeses = ref(12);
    const maxMes = computed(() => Math.max(...mesesAsc.value.map((m) => m.total), 1));
    const mediaHistorica = computed(() => {
      const cerrados = mesesAsc.value.filter((m) => m.mes !== mesHoy);
      return cerrados.length ? cerrados.reduce((s, m) => s + m.total, 0) / cerrados.length : 0;
    });

    // ---------- Lista ----------
    const gastosLista = computed(() => gastosMes.value.filter((g) =>
      (!diaSel.value || diaISO(g) === diaSel.value) && (!catSel.value || categoriaDe(g) === catSel.value)
    ));
    const tituloLista = computed(() => `Gastos de ${nombreMes(mesSel.value)}`);

    const fechaCorta = (iso) => parseISO(iso).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

    onMounted(async () => {
      const tareas = [
        axios.get('/api/gastos/todos').then(({ data }) => store.commit('setGastos', data)).catch((e) => console.error('Error al cargar los gastos:', e))
      ];
      if (hasIngresos.value) {
        tareas.push(axios.get('/api/ingresos/stats/por-mes').then(({ data }) => { ingresosRaw.value = data; }).catch(() => { ingresosRaw.value = []; }));
      }
      await Promise.all(tareas);
    });

    return {
      hasIngresos, hayIngresos, ingresosPorMes, colores, colorCat,
      mesesDesc, mesSel, diaSel, catSel, moverMes, mesPrevDisponible, mesSigDisponible, toggleCat,
      esMesEnCurso, gastosMes, totalMes, ingresosMes, diasTranscurridos, mediaDiariaMes,
      totalesDiaMes, diasConGasto, diaMasCaro, rankingMes, catMesAnterior, deltaCatMes, claseDelta,
      deltaMesAnterior, deltaAnoPasado, fraseHero, insights,
      ventanas, ventana, mediaVentana, pulsoData, pulsoOptions,
      carreraData, carreraOptions,
      podio, limiteMeses, maxMes, mediaHistorica,
      gastosLista, tituloLista,
      euros, importe, diaISO, categoriaDe, textoVariacion, nombreMes, mesAnterior, fechaCorta
    };
  }
};
</script>

<style scoped>
.min-w-0 { min-width: 0; }
.ag-meses { display: flex; flex-direction: column; gap: 2px; max-height: 420px; overflow-y: auto; }
.ag-meses__fila {
  display: grid;
  grid-template-columns: 4.2rem minmax(0, 1fr) 4.8rem auto;
  gap: 0.6rem;
  align-items: center;
  border: 0;
  background: transparent;
  border-radius: 0.5rem;
  padding: 0.3rem 0.4rem;
  text-align: left;
  color: var(--tinta);
}
.ag-meses__fila:hover { background: rgba(22, 53, 40, 0.04); }
.ag-meses__fila.activo { background: var(--marca-suave); }
.ag-meses__nombre { font-size: 0.85rem; font-weight: 600; }
.ag-meses__barra { position: relative; height: 10px; background: rgba(22, 53, 40, 0.06); border-radius: 999px; }
.ag-meses__barra > span { position: absolute; inset: 0 auto 0 0; background: #8cc3a0; border-radius: 999px; transition: width 0.5s ease; }
.ag-meses__barra > span.alto { background: #eb6834; }
.ag-meses__fila.activo .ag-meses__barra > span { background: #1f6b45; }
.ag-meses__barra > i { position: absolute; top: -3px; bottom: -3px; width: 2px; background: var(--tinta-suave); opacity: 0.5; border-radius: 1px; }
.ag-meses__valor { text-align: right; font-size: 0.9rem; }
.ag-meses__balance { font-size: 0.78rem; font-weight: 600; min-width: 4.5rem; text-align: right; }
.ag-meses__balance.pos { color: var(--ingreso); }
.ag-meses__balance.neg { color: var(--urgente); }
</style>
